import { useCallback, useEffect, useRef, useState } from 'react';
import { FeatureLayer, Graphic, WebMap } from '../constructors';
import { EntityIdToObjectIdMaps } from '../helpers/entity-id-to-object-id-maps';
import { createSensorFeatureLayer } from '../helpers/feature-layers';
import { createSensorGraphic } from '../helpers/graphics';
import { useLayerControl } from './use-feature-layer-control';
import type { MappableSensor } from '../../sensors/types';
import { useSensorsEnabled } from '../../sensors/use-sensors-enabled';

type SensorFeatureLayerProps = {
    mappableSensors: MappableSensor[];
    webMap?: WebMap;
};

export const useSensorFeatureLayer = ({ mappableSensors, webMap }: SensorFeatureLayerProps) => {
    const sensorsEnabled = useSensorsEnabled();
    const entityIdToObjectIdMapCacheRef = useRef<typeof EntityIdToObjectIdMaps.SENSORS>(
        EntityIdToObjectIdMaps.SENSORS
    );
    const [sensorsLayer, setSensorsLayer] = useState<FeatureLayer>();

    // A camera graphic only needs rebuilding when something it draws changes. There is no map-level
    // selection state for cameras — their detail lives in a workspace panel — so position, name and
    // status are the whole dependency set.
    const didSensorGraphicChange = useCallback(
        (currSensor: MappableSensor, prevSensor: MappableSensor) => {
            return (
                currSensor.latitude !== prevSensor.latitude ||
                currSensor.longitude !== prevSensor.longitude ||
                currSensor.online !== prevSensor.online ||
                currSensor.name !== prevSensor.name
            );
        },
        []
    );

    const createSensorGraphics = useCallback((sensorsToDraw: MappableSensor[]) => {
        const sensorGraphics: Graphic[] = [];
        for (const mappableSensor of sensorsToDraw) {
            const objectId = entityIdToObjectIdMapCacheRef.current.get(mappableSensor.entityId);
            const sensorGraphic = createSensorGraphic({ objectId, mappableSensor });
            if (sensorGraphic) {
                sensorGraphics.push(sensorGraphic);
            }
        }
        return sensorGraphics;
    }, []);

    // handles initializing empty feature layer. Only with sensors on: otherwise every map, First
    // Responder's included, would carry an empty layer that joins every hover and click hit test.
    useEffect(
        function addSensorLayerToWebMap() {
            if (webMap && sensorsEnabled) {
                const sensorFeatureLayer = createSensorFeatureLayer();
                setSensorsLayer(sensorFeatureLayer);
                webMap.layers.add(sensorFeatureLayer);
            }
        },
        [webMap, sensorsEnabled]
    );

    useLayerControl<MappableSensor>({
        layer: sensorsLayer,
        currItems: mappableSensors,
        didItemChange: didSensorGraphicChange,
        createGraphics: createSensorGraphics,
        entityIdToObjectIdMapCacheRef,
    });

    return { sensorsLayer };
};
